<template>
  <div class="nx-relation-card">
    <v-progress-linear
      v-show="loading"
      indeterminate
      background-opacity="0"
      height="2"
    />
    <v-layout class="row wrap">
      <v-flex class="xs12 px-1">
        <nx-form-scope-generator
          :config="formConfig"
          :scope="scope"
          :disabled="disabled"
          :excluded-content-ids="excludedEntityIds"
          :value="value"
          @input="updateField"
          hide-heading
        ></nx-form-scope-generator>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import RelationCardForm from '@/modules/relation/card/form/RelationCardForm';
import cloneDeep from 'lodash/cloneDeep';
import { toArray } from '@/utils';
import { isArray, isEmpty, isObject } from 'lodash';
import { getPopulateFieldsForRelation } from '@/modules/entity/api';

export default {
  name: 'nx-relation-auto-generated-card-form',
  extends: RelationCardForm,
  props: {
    formConfig: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
    },
  },
  computed: {
    fieldsConfig() {
      return this.formConfig.fieldsConfig || this.formConfig || [];
    },
  },
  methods: {
    fieldConfig(field) {
      return this.fieldsConfig.find(config => config.field === field) || {};
    },
    fieldTriggerPopulation(field, value) {
      if (this.fieldConfig(field).triggerPopulation === false) {
        return false;
      }
      if (isArray(value)) {
        return value.length > 0;
      }
      return Boolean(value);
    },
    fieldUsePopulateFieldsCmsApiService(field) {
      return this.fieldConfig(field).usePopulateFieldsCmsApiService === true;
    },
    fieldIgnorePopulation(field) {
      return this.fieldConfig(field).ignorePopulation === true;
    },
    /**
     * Updates a field from the draft with the given value.
     * If a more specific handler is registered on `updateSpecificField`, this handler will be
     * called and its result will be used as the new draft value.
     *
     * @param {string}   field
     * @param {*}        value
     * @param {Array<*>} args
     *
     * @return {Promise<*>} A promise that resolves with the updated draft value.
     */
    async updateField(field, value, ...args) {
      let draft = {};
      const { defaultUpdate = false } = isObject(args[0]) ? args[0] : {};
      if (this.fieldTriggerPopulation(field, value)) {
        this.loading = true;
        draft = await this.handleTriggerPopulation(field, value, ...args);
        this.loading = false;
      } else {
        draft = cloneDeep(this.value);
        draft[field] = value;
      }
      this.$emit(defaultUpdate ? 'default' : 'input', this.uuid, draft);
      if (!defaultUpdate) {
        // Wait next tick to validate card scope and relation card errors
        this.$nextTick(() => {
          this.$validator.validate(`${this.scope}.*`);
        });
      }
      return draft;
    },


    async handleTriggerPopulation(field, value, ...args) {
      const draft = { ...cloneDeep(this.value), [field]: value };
      const entity = (isArray(args) && isObject(args[0])) ? args[0] : {};
      let results = this.fieldUsePopulateFieldsCmsApiService(field)
        ? await getPopulateFieldsForRelation(value, entity.type, {
          triggerPopulationField: field,
          contextEntity: this.$getParentEditor ? this.$getParentEditor().type || this.$getParentEditor().entity : null,
          contextEntityId: this.$getParentEditor ? this.$getParentEditor().id : null,
          relationType: this.relationName,
          draft: this.draft ? this.draft : {},
        })
        : await this.getConfigGeneratedFields(field, toArray(value), ...args);

      if (Array.isArray(results)
        && results.length > 0) {
        results = results[0];
      }

      if (isEmpty(results)) {
        // Couldn't get the relation fields. Update the field at least.
        return draft;
      }

      const population = Object
        .keys(results)
        // Filter the fields to be populated by config
        // Ignore fields configured as "ignorePopulation"
        .filter((key) => {
          if (field === key) {
            return true;
          }
          return this.fieldIgnorePopulation(key) !== true;
        })
        .reduce((result, key) => {
          result[key] = results[key];
          return result;
        }, {});

      Object.entries(population).forEach(([key, val]) => {
        Object.assign(draft, { [key]: val });
      });
      return draft;
    },
  },
};
</script>
